import React, { useState, useEffect } from 'react';
// Updated imports for ethers v6 - import individual functions instead of namespaces
import { 
  Contract, 
  WebSocketProvider, 
  JsonRpcProvider,
  BrowserProvider, // replaces Web3Provider
  formatEther,
  parseEther,
  randomBytes,
  hexlify,
  solidityPacked, // replaces solidityPack
  keccak256,
  toUtf8Bytes,
  getAddress,
  isAddress,
  formatUnits,
  parseUnits
} from 'ethers';

const CONTRACT_ADDRESS = '0xAf0752C7fD91952A941e088b9C7E72C082F56BB3';
const SONIC_TOKEN = '0x0000000000000000000000000000000000000000';
const WRAPPED_SONIC = '0x039e2fB66102314Ce7b64Ce5Ce3E5183bc94aD38';
const OWNER_ADDRESS = '0x7f11350A04C87425117c885656df0AF0295E2780';

// Simplified ABI for the CoinFlip contract
const CONTRACT_ABI = [
  "function createGame(bytes32 commitHash, bool choice) external payable",
  "function playAgainstBot(uint256 gameId) external",
  "function revealAndSettle(uint256 gameId, uint256 playerSeed, bytes32 salt) external",
  "function cancelGame(uint256 gameId) external",
  "function games(uint256 gameId) external view returns (address player, uint256 betAmount, bool playerChoice, bytes32 commitHash, uint256 commitBlock, bool isComplete, bool playerWon, uint256 revealedSeed, address opponent, bool isBot, uint256 opponentSeed, address tokenAddress)",
  "function nextGameId() external view returns (uint256)",
  "function getRiskInfo(address tokenAddress) external view returns (uint256 maxBet, uint256 currentRisk, uint256 availableLiquidity, uint256 totalLiquidity, uint256 activeBetsAmount)",
  "function owner() external view returns (address)",
  "function allowedTokens(address) external view returns (bool)",
  "function setAllowedToken(address tokenAddress, bool allowed) external",
  "function addLiquidity() external payable",
  "function addTokenLiquidity(address tokenAddress, uint256 amount) external",
  "function withdrawLiquidity(uint256 amount) external",
  "function withdrawTokenLiquidity(address tokenAddress, uint256 amount) external",
  "event GameCreated(uint256 indexed gameId, address indexed player, address tokenAddress, uint256 betAmount, bool choice, bytes32 commitHash)",
  "event GameJoined(uint256 indexed gameId, address indexed opponent, uint256 opponentSeed)",
  "event GameReveal(uint256 indexed gameId, bool result, uint256 playerSeed, uint256 opponentSeed)",
  "event GameSettled(uint256 indexed gameId, address winner, address tokenAddress, uint256 payout)",
  "event LiquidityAdded(address indexed provider, address tokenAddress, uint256 amount)",
  "event LiquidityWithdrawn(address indexed provider, address tokenAddress, uint256 amount)",
  "event TokenAllowed(address tokenAddress, bool allowed)"
];

// ERC20 Token ABI (minimal for what we need)
const ERC20_ABI = [
  "function balanceOf(address account) external view returns (uint256)",
  "function decimals() external view returns (uint8)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transfer(address recipient, uint256 amount) external returns (bool)",
  "function transferFrom(address sender, address recipient, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function symbol() external view returns (string)"
];

// Main component
const CoinFlipApp = () => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [contract, setContract] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [betAmount, setBetAmount] = useState('0.1');
  const [choice, setChoice] = useState(true); // true = heads, false = tails
  const [activeGames, setActiveGames] = useState([]);
  const [completedGames, setCompletedGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextGameId, setNextGameId] = useState(1);
  const [riskInfo, setRiskInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState('play');
  const [selectedGame, setSelectedGame] = useState(null);
  const [savedSeeds, setSavedSeeds] = useState({});
  const [seedValue, setSeedValue] = useState('');
  const [salt, setSalt] = useState('');
  
  // Admin state variables
  const [allTokens, setAllTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(SONIC_TOKEN);
  const [liquidityAmount, setLiquidityAmount] = useState('1');
  const [withdrawAmount, setWithdrawAmount] = useState('0.5');
  const [newTokenAddress, setNewTokenAddress] = useState('');
  const [newTokenAllowed, setNewTokenAllowed] = useState(true);
  const [tokenDecimals, setTokenDecimals] = useState({});
  
  // Function to connect to wallet
  const connectWallet = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (!window.ethereum) {
        throw new Error("Metamask not detected. Please install Metamask to use this DApp.");
      }
      
      // Request account access
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      // Check if connected to Sonic network
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (chainId !== '0x92') { // 146 in hex is 0x92
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x92' }],
          });
        } catch (switchError) {
          // This error code indicates that the chain has not been added to MetaMask
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: '0x92',
                chainName: 'Sonic',
                nativeCurrency: {
                  name: 'Sonic',
                  symbol: 'S',
                  decimals: 18
                },
                rpcUrls: ['https://rpc.soniclabs.com'],
                blockExplorerUrls: ['https://sonicscan.org']
              }]
            });
          } else {
            throw switchError;
          }
        }
      }
      
      // Updated to use BrowserProvider instead of Web3Provider
      const ethProvider = new BrowserProvider(window.ethereum);
      const signer = await ethProvider.getSigner(); // getSigner() is now async in v6
      const gameContract = new Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
      
      const connectedAccount = accounts[0];
      setAccount(connectedAccount);
      setProvider(ethProvider);
      setContract(gameContract);
      setIsConnected(true);
      
      // Check if connected account is the owner
      const contractOwner = await gameContract.owner();
      const isOwnerWallet = getAddress(connectedAccount) === getAddress(contractOwner);
      setIsOwner(isOwnerWallet);

      if (isOwnerWallet) {
        // Load token list if owner
        await loadTokenList(gameContract);
      }
      
      // Load contract data
      await loadContractData(gameContract, connectedAccount);
      
      // Setup event listeners for MetaMask
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', () => window.location.reload());
      
    } catch (err) {
      console.error("Error connecting wallet:", err);
      setError(err.message || "Failed to connect wallet");
    } finally {
      setLoading(false);
    }
  };

  // Get token information including decimals
  const getTokenInfo = async (tokenAddress) => {
    try {
      // Skip for native token
      if (tokenAddress === SONIC_TOKEN) {
        return {
          decimals: 18,
          symbol: "S",
          name: "Sonic"
        };
      }
      
      const tokenContract = new Contract(tokenAddress, ERC20_ABI, provider);
      const decimals = await tokenContract.decimals();
      let symbol = "TOKEN";
      
      try {
        symbol = await tokenContract.symbol();
      } catch (err) {
        console.warn(`Could not get symbol for token ${tokenAddress}`, err);
      }
      
      return {
        decimals: Number(decimals),
        symbol,
        name: symbol
      };
    } catch (err) {
      console.error(`Error getting token info for ${tokenAddress}:`, err);
      return {
        decimals: 18, // Default to 18 decimals
        symbol: "UNKNOWN",
        name: "Unknown Token"
      };
    }
  };
  
  // Load list of allowed tokens (for admin)
  const loadTokenList = async (gameContract) => {
    try {
      // Start with the two default tokens
      const tokenList = [
        {
          address: SONIC_TOKEN,
          name: "Sonic",
          symbol: "S",
          allowed: true,
          decimals: 18
        },
        {
          address: WRAPPED_SONIC,
          name: "Wrapped Sonic",
          symbol: "wS",
          allowed: await gameContract.allowedTokens(WRAPPED_SONIC),
          decimals: 18 // Wrapped Sonic has 18 decimals
        }
      ];
      
      // Add any additional tokens you want to check here
      const additionalTokenAddresses = [
        // Example: "0x123..."
      ];
      
      for (const tokenAddress of additionalTokenAddresses) {
        try {
          const isAllowed = await gameContract.allowedTokens(tokenAddress);
          const tokenInfo = await getTokenInfo(tokenAddress);
          
          tokenList.push({
            address: tokenAddress,
            name: tokenInfo.name,
            symbol: tokenInfo.symbol,
            allowed: isAllowed,
            decimals: tokenInfo.decimals
          });
          
          // Store token decimals for later use
          setTokenDecimals(prev => ({
            ...prev,
            [tokenAddress]: tokenInfo.decimals
          }));
        } catch (err) {
          console.error(`Error checking token ${tokenAddress}:`, err);
        }
      }
      
      setAllTokens(tokenList);
      
      // Store token decimals
      const decimalsMap = {};
      tokenList.forEach(token => {
        decimalsMap[token.address] = token.decimals;
      });
      setTokenDecimals(decimalsMap);
      
      // Set default selected token to Sonic
      setSelectedToken(SONIC_TOKEN);
      
    } catch (err) {
      console.error("Error loading token list:", err);
    }
  };
  
  // Handle account change
  const handleAccountsChanged = async (accounts) => {
    if (accounts.length === 0) {
      // User disconnected
      setIsConnected(false);
      setAccount(null);
      setIsOwner(false);
    } else {
      // User switched account
      const newAccount = accounts[0];
      setAccount(newAccount);
      
      if (contract) {
        // Check if new account is owner
        try {
          const contractOwner = await contract.owner();
          const isOwnerWallet = getAddress(newAccount) === getAddress(contractOwner);
          setIsOwner(isOwnerWallet);
          
          if (isOwnerWallet) {
            // Load token list if owner
            await loadTokenList(contract);
          }
        } catch (err) {
          console.error("Error checking owner:", err);
        }
        
        await loadContractData(contract, newAccount);
      }
    }
  };

  // Load contract data
  const loadContractData = async (gameContract, userAccount) => {
    try {
      setLoading(true);
      
      // Get next game ID
      const nextId = await gameContract.nextGameId();
      setNextGameId(Number(nextId)); // In v6, use Number() instead of toNumber()
      
      // Get risk info for Sonic
      const risk = await gameContract.getRiskInfo(SONIC_TOKEN);
      setRiskInfo({
        maxBet: formatEther(risk.maxBet), // Updated to use imported formatEther
        currentRisk: Number(risk.currentRisk), // In v6, use Number() instead of toNumber()
        availableLiquidity: formatEther(risk.availableLiquidity),
        totalLiquidity: formatEther(risk.totalLiquidity),
        activeBetsAmount: formatEther(risk.activeBetsAmount)
      });
      
      // Load user's games
      const userActiveGames = [];
      const userCompletedGames = [];
      
      // Loop through all games (up to nextId)
      for (let i = 1; i < Number(nextId); i++) {
        try {
          const game = await gameContract.games(i);
          if (game.player.toLowerCase() === userAccount.toLowerCase() || 
              (game.opponent && game.opponent.toLowerCase() === userAccount.toLowerCase())) {
            
            const gameData = {
              id: i,
              player: game.player,
              betAmount: formatEther(game.betAmount),
              playerChoice: game.playerChoice ? 'Heads' : 'Tails',
              isComplete: game.isComplete,
              playerWon: game.playerWon,
              opponent: game.opponent,
              isBot: game.isBot,
              tokenAddress: game.tokenAddress
            };
            
            if (game.isComplete) {
              userCompletedGames.push(gameData);
            } else {
              userActiveGames.push(gameData);
            }
          }
        } catch (err) {
          console.error(`Error loading game ${i}:`, err);
        }
      }
      
      setActiveGames(userActiveGames);
      setCompletedGames(userCompletedGames);
      
      // Load saved seeds from localStorage
      const saved = JSON.parse(localStorage.getItem(`coinflip_seeds_${userAccount}`) || '{}');
      setSavedSeeds(saved);
      
    } catch (err) {
      console.error("Error loading contract data:", err);
      setError("Failed to load game data");
    } finally {
      setLoading(false);
    }
  };

  // Generate random seed and commit hash for new game
  const generateSeedAndCommit = () => {
    // Generate a random seed (32-bit unsigned integer)
    const seed = Math.floor(Math.random() * 4294967296);
    
    // Generate a random salt - updated for v6
    const saltBytes = randomBytes(32);
    const saltHex = hexlify(saltBytes);
    
    // Calculate the commit hash - updated for v6
    // Use solidityPacked instead of solidityPack
    const abiEncoded = solidityPacked(['uint256', 'bytes32'], [seed, saltHex]);
    const commitHash = keccak256(abiEncoded);
    
    return { seed, salt: saltHex, commitHash };
  };

  // Create a new game
  const createGame = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (!isConnected) {
        throw new Error("Please connect your wallet first");
      }
      
      // Generate seed, salt and commit hash
      const { seed, salt, commitHash } = generateSeedAndCommit();
      
      // Convert bet amount to wei - updated for v6
      const betAmountWei = parseEther(betAmount);
      
      // Create game
      const tx = await contract.createGame(commitHash, choice, {
        value: betAmountWei
      });
      
      // Wait for transaction to be mined
      const receipt = await tx.wait();
      
      // Find the GameCreated event in the receipt - updated for v6
      // In v6, events are accessed differently
      const gameCreatedEvent = receipt.logs
        .filter(log => log.fragment && log.fragment.name === 'GameCreated')
        .map(log => ({
          event: log.fragment.name,
          args: log.args
        }))[0];
      
      if (!gameCreatedEvent) {
        throw new Error("Game creation event not found in transaction");
      }
      
      // Get the game ID from the event
      const gameId = Number(gameCreatedEvent.args[0]); // First indexed arg is gameId
      
      // Save the seed and salt for later
      const updatedSeeds = {
        ...savedSeeds,
        [gameId]: { seed, salt }
      };
      setSavedSeeds(updatedSeeds);
      
      // Store in local storage
      localStorage.setItem(`coinflip_seeds_${account}`, JSON.stringify(updatedSeeds));
      
      // Reload contract data
      await loadContractData(contract, account);
      
      // Set alert message
      alert(`Game created successfully! Game ID: ${gameId}\n\nIMPORTANT: Save your seed (${seed}) and salt. You'll need these to reveal and settle the game later.`);
      
    } catch (err) {
      console.error("Error creating game:", err);
      setError(err.message || "Failed to create game");
    } finally {
      setLoading(false);
    }
  };

  // Play against bot
  const playBot = async (gameId) => {
    try {
      setError(null);
      setLoading(true);
      
      // Call playAgainstBot function
      const tx = await contract.playAgainstBot(gameId);
      await tx.wait();
      
      // Reload contract data
      await loadContractData(contract, account);
      
      alert("Bot has joined your game! Now you can reveal and settle.");
      
    } catch (err) {
      console.error("Error playing against bot:", err);
      setError(err.message || "Failed to play against bot");
    } finally {
      setLoading(false);
    }
  };

  // Reveal and settle game
  const revealAndSettle = async (gameId, manualSeed, manualSalt) => {
    try {
      setError(null);
      setLoading(true);
      
      // Get the seed and salt from saved data or manual input
      let seedToUse, saltToUse;
      
      if (manualSeed && manualSalt) {
        seedToUse = manualSeed;
        saltToUse = manualSalt;
      } else if (savedSeeds[gameId]) {
        seedToUse = savedSeeds[gameId].seed;
        saltToUse = savedSeeds[gameId].salt;
      } else {
        throw new Error("No seed and salt found for this game");
      }
      
      // Call revealAndSettle function
      const tx = await contract.revealAndSettle(gameId, seedToUse, saltToUse);
      await tx.wait();
      
      // Reload contract data
      await loadContractData(contract, account);
      
      alert("Game revealed and settled successfully!");
      
    } catch (err) {
      console.error("Error revealing game:", err);
      setError(err.message || "Failed to reveal and settle game");
    } finally {
      setLoading(false);
    }
  };

  // Cancel game
  const cancelGame = async (gameId) => {
    try {
      setError(null);
      setLoading(true);
      
      // Call cancelGame function
      const tx = await contract.cancelGame(gameId);
      await tx.wait();
      
      // Reload contract data
      await loadContractData(contract, account);
      
      alert("Game cancelled successfully!");
      
    } catch (err) {
      console.error("Error cancelling game:", err);
      setError(err.message || "Failed to cancel game");
    } finally {
      setLoading(false);
    }
  };

  // Add admin functions
  const addLiquidity = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (selectedToken === SONIC_TOKEN) {
        // Add native Sonic liquidity
        const valueWei = parseEther(liquidityAmount);
        const tx = await contract.addLiquidity({ value: valueWei });
        await tx.wait();
        alert(`Added ${liquidityAmount} Sonic to liquidity pool successfully!`);
      } else {
        // Add token liquidity
        // First need to approve the contract to spend tokens
        const selectedTokenInfo = allTokens.find(t => t.address === selectedToken);
        
        // Get decimals for the token
        const decimals = tokenDecimals[selectedToken] || 18;
        
        // Calculate amount in token decimals
        const amount = parseUnits(liquidityAmount, decimals);
        
        // Create token contract
        const tokenContract = new Contract(selectedToken, ERC20_ABI, await provider.getSigner());
        
        // Approve the contract to spend tokens
        const approveTx = await tokenContract.approve(CONTRACT_ADDRESS, amount);
        await approveTx.wait();
        
        // Add liquidity
        const tx = await contract.addTokenLiquidity(selectedToken, amount);
        await tx.wait();
        
        alert(`Added ${liquidityAmount} ${selectedTokenInfo?.symbol || 'tokens'} to liquidity pool successfully!`);
      }
      
      // Refresh data
      await loadContractData(contract, account);
      
    } catch (err) {
      console.error("Error adding liquidity:", err);
      setError(err.message || "Failed to add liquidity");
    } finally {
      setLoading(false);
    }
  };
  
  const withdrawLiquidity = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (selectedToken === SONIC_TOKEN) {
        // Withdraw native Sonic
        const valueWei = parseEther(withdrawAmount);
        const tx = await contract.withdrawLiquidity(valueWei);
        await tx.wait();
        alert(`Withdrawn ${withdrawAmount} Sonic from liquidity pool successfully!`);
      } else {
        // Withdraw token
        const selectedTokenInfo = allTokens.find(t => t.address === selectedToken);
        
        // Get decimals for the token
        const decimals = tokenDecimals[selectedToken] || 18;
        
        // Calculate amount in token decimals
        const amount = parseUnits(withdrawAmount, decimals);
        
        // Withdraw
        const tx = await contract.withdrawTokenLiquidity(selectedToken, amount);
        await tx.wait();
        
        alert(`Withdrawn ${withdrawAmount} ${selectedTokenInfo?.symbol || 'tokens'} from liquidity pool successfully!`);
      }
      
      // Refresh data
      await loadContractData(contract, account);
      
    } catch (err) {
      console.error("Error withdrawing liquidity:", err);
      setError(err.message || "Failed to withdraw liquidity");
    } finally {
      setLoading(false);
    }
  };
  
  const setTokenAllowed = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (!isAddress(newTokenAddress)) {
        throw new Error("Invalid token address");
      }
      
      const tx = await contract.setAllowedToken(newTokenAddress, newTokenAllowed);
      await tx.wait();
      
      alert(`Token ${newTokenAddress} ${newTokenAllowed ? 'allowed' : 'disallowed'} successfully!`);
      
      // Refresh token list
      await loadTokenList(contract);
      
      // Reset form
      setNewTokenAddress('');
      
    } catch (err) {
      console.error("Error setting token allowed:", err);
      setError(err.message || "Failed to set token allowed status");
    } finally {
      setLoading(false);
    }
  };

  // Render Play tab
  const renderPlayTab = () => (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700">
      <h2 className="text-xl font-bold mb-5 text-blue-300">Create New Game</h2>
      
      {/* Bet amount input */}
      <div className="mb-5">
        <label className="block text-sm font-medium mb-2 text-gray-300">Bet Amount (S)</label>
        <input
          type="number"
          value={betAmount}
          onChange={(e) => setBetAmount(e.target.value)}
          min="0.1"
          step="0.1"
          className="w-full p-3 bg-gray-700 border border-gray-600 focus:border-blue-500 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        {riskInfo && (
          <p className="text-sm text-blue-300 mt-2">
            Max bet: {riskInfo.maxBet} S
          </p>
        )}
      </div>
      
      {/* Coin side selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2 text-gray-300">Choose Side</label>
        <div className="flex space-x-4">
          <button
            onClick={() => setChoice(true)}
            className={`flex-1 p-3 rounded-md shadow-sm transition-colors duration-200 ${
              choice 
                ? 'bg-blue-700 text-white border border-blue-600' 
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            Heads
          </button>
          <button
            onClick={() => setChoice(false)}
            className={`flex-1 p-3 rounded-md shadow-sm transition-colors duration-200 ${
              !choice 
                ? 'bg-blue-700 text-white border border-blue-600' 
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            Tails
          </button>
        </div>
      </div>
      
      {/* Create game button */}
      <button
        onClick={createGame}
        disabled={loading || !isConnected}
        className="w-full bg-blue-700 hover:bg-blue-600 text-white p-3 rounded-md font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 border border-blue-600"
      >
        {loading ? "Processing..." : "Create Game"}
      </button>
    </div>
  );

  // Render My Games tab
  const renderMyGamesTab = () => (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700">
      <h2 className="text-xl font-bold mb-5 text-blue-300">My Games</h2>
      
      {/* Active games */}
      <div className="mb-8">
        <h3 className="font-bold text-lg mb-3 text-gray-200 border-b border-gray-700 pb-2">Active Games</h3>
        {activeGames.length === 0 ? (
          <p className="text-gray-400 py-3">No active games found.</p>
        ) : (
          <div className="space-y-4">
            {activeGames.map(game => (
              <div key={game.id} className="border border-gray-700 bg-gray-750 p-4 rounded-lg shadow-sm hover:border-gray-600 transition-colors duration-200">
                <div className="flex justify-between mb-3">
                  <span className="font-medium text-blue-300">Game #{game.id}</span>
                  <span className="text-blue-400 font-medium">{game.betAmount} S</span>
                </div>
                <div className="text-sm text-gray-400 mb-3">
                  <div>Choice: <span className="text-gray-300">{game.playerChoice}</span></div>
                  <div>Opponent: <span className="text-gray-300">
                    {game.opponent === '0x0000000000000000000000000000000000000000' 
                      ? 'Waiting...' 
                      : (game.isBot ? 'Bot' : 'Player')}
                  </span></div>
                </div>
                <div className="flex space-x-3">
                  {game.opponent === '0x0000000000000000000000000000000000000000' && (
                    <>
                      <button
                        onClick={() => playBot(game.id)}
                        className="flex-1 bg-blue-700 hover:bg-blue-600 border border-blue-600 text-white p-2 rounded-md text-sm shadow-sm transition-colors duration-200"
                      >
                        Play vs Bot
                      </button>
                      <button
                        onClick={() => cancelGame(game.id)}
                        className="flex-1 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 p-2 rounded-md text-sm shadow-sm transition-colors duration-200"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {game.opponent !== '0x0000000000000000000000000000000000000000' && (
                    <button
                      onClick={() => {
                        setSelectedGame(game);
                        if (savedSeeds[game.id]) {
                          setSeedValue(savedSeeds[game.id].seed);
                          setSalt(savedSeeds[game.id].salt);
                        } else {
                          setSeedValue('');
                          setSalt('');
                        }
                      }}
                      className="flex-1 bg-blue-700 hover:bg-blue-600 border border-blue-600 text-white p-2 rounded-md text-sm shadow-sm transition-colors duration-200"
                    >
                      Reveal & Settle
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Completed games */}
      <div>
        <h3 className="font-bold text-lg mb-3 text-gray-200 border-b border-gray-700 pb-2">Game History</h3>
        {completedGames.length === 0 ? (
          <p className="text-gray-400 py-3">No completed games found.</p>
        ) : (
          <div className="space-y-4">
            {completedGames.map(game => (
              <div key={game.id} className="border border-gray-700 bg-gray-750 p-4 rounded-lg shadow-sm">
                <div className="flex justify-between mb-3">
                  <span className="font-medium text-blue-300">Game #{game.id}</span>
                  <span className="text-blue-400 font-medium">{game.betAmount} S</span>
                </div>
                <div className="text-sm text-gray-400 mb-2">
                  <div>Choice: <span className="text-gray-300">{game.playerChoice}</span></div>
                  <div>Opponent: <span className="text-gray-300">{game.isBot ? 'Bot' : 'Player'}</span></div>
                </div>
                <div className={`font-bold ${game.playerWon ? 'text-blue-400' : 'text-gray-400'}`}>
                  {game.playerWon ? '✨ You Won!' : 'You Lost'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Reveal dialog */}
      {selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-blue-300">Reveal Game #{selectedGame.id}</h3>
            
            {savedSeeds[selectedGame.id] ? (
              <div className="mb-5">
                <p className="text-blue-400 mb-3">✅ Seed and salt found for this game!</p>
                <p className="text-sm text-gray-300 bg-gray-700 p-3 rounded-md border border-gray-600">
                  Seed: <span className="text-blue-300 font-mono">{savedSeeds[selectedGame.id].seed}</span><br />
                  Salt: <span className="text-blue-300 font-mono">{savedSeeds[selectedGame.id].salt.substring(0, 10)}...</span>
                </p>
              </div>
            ) : (
              <div className="mb-5">
                <p className="text-yellow-400 mb-4">No saved seed and salt found for this game. Please enter them manually:</p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 text-gray-300">Seed (number)</label>
                  <input
                    type="number"
                    value={seedValue}
                    onChange={(e) => setSeedValue(e.target.value)}
                    className="w-full p-3 bg-gray-700 border border-gray-600 focus:border-blue-500 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 text-gray-300">Salt (32 byte hex)</label>
                  <input
                    type="text"
                    value={salt}
                    onChange={(e) => setSalt(e.target.value)}
                    className="w-full p-3 bg-gray-700 border border-gray-600 focus:border-blue-500 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                    placeholder="0x..."
                  />
                </div>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={() => revealAndSettle(selectedGame.id, seedValue, salt)}
                className="flex-1 bg-blue-700 hover:bg-blue-600 border border-blue-600 text-white p-3 rounded-md transition-colors duration-200 shadow-sm"
              >
                Reveal & Settle
              </button>
              <button
                onClick={() => setSelectedGame(null)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 p-3 rounded-md transition-colors duration-200 shadow-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Info tab
  const renderInfoTab = () => (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700">
      <h2 className="text-xl font-bold mb-5 text-blue-300">Game Information</h2>
      
      {riskInfo ? (
        <div className="space-y-5">
          <div className="bg-gray-750 border border-blue-900 p-4 rounded-md shadow-sm">
            <h3 className="font-medium text-blue-300 mb-2">Liquidity</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm text-gray-400">Total Liquidity:</div>
              <div className="text-sm font-medium text-right text-blue-300">{riskInfo.totalLiquidity} S</div>
              
              <div className="text-sm text-gray-400">Available Liquidity:</div>
              <div className="text-sm font-medium text-right text-blue-300">{riskInfo.availableLiquidity} S</div>
              
              <div className="text-sm text-gray-400">Active Bets:</div>
              <div className="text-sm font-medium text-right text-blue-300">{riskInfo.activeBetsAmount} S</div>
            </div>
          </div>
          
          <div className="bg-gray-750 border border-blue-900 p-4 rounded-md shadow-sm">
            <h3 className="font-medium text-blue-300 mb-2">Risk Management</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm text-gray-400">Max Bet Allowed:</div>
              <div className="text-sm font-medium text-right text-blue-300">{riskInfo.maxBet} S</div>
              
              <div className="text-sm text-gray-400">Current Risk:</div>
              <div className="text-sm font-medium text-right text-blue-300">{riskInfo.currentRisk}%</div>
            </div>
          </div>
          
          <div className="bg-gray-750 border border-blue-900 p-4 rounded-md shadow-sm">
            <h3 className="font-medium text-blue-300 mb-2">Game Stats</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm text-gray-400">Total Games Created:</div>
              <div className="text-sm font-medium text-right text-blue-300">{nextGameId - 1}</div>
              
              <div className="text-sm text-gray-400">House Edge:</div>
              <div className="text-sm font-medium text-right text-blue-300">5%</div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-400 py-4">Loading information...</p>
      )}
      
      <div className="mt-8 border-t border-gray-700 pt-6">
        <h3 className="font-bold text-lg mb-4 text-gray-200">How to Play</h3>
        <ol className="list-decimal list-inside space-y-3 text-gray-300">
          <li>Connect your wallet to Sonic Network <span className="text-gray-400">(Chain ID: 146)</span></li>
          <li>Enter your bet amount <span className="text-gray-400">(min 0.1 S)</span> and choose Heads or Tails</li>
          <li>Create a game by clicking <span className="text-blue-400">"Create Game"</span></li>
          <li>Play against the bot by clicking <span className="text-blue-400">"Play vs Bot"</span> or wait for another player</li>
          <li>After the game is played, reveal your seed by clicking <span className="text-blue-400">"Reveal & Settle"</span></li>
          <li>If you win, your winnings <span className="text-gray-400">(minus 5% fee)</span> will be sent to your wallet</li>
        </ol>
      </div>
    </div>
  );

  // Render Admin tab
  const renderAdminTab = () => (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700">
      <h2 className="text-xl font-bold mb-5 text-blue-300">Admin Panel</h2>
      
      {/* Add Liquidity Section */}
      <div className="mb-8 border-b border-gray-700 pb-6">
        <h3 className="text-lg font-bold mb-4 text-gray-200">Add Liquidity</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-300">Token</label>
          <select 
            value={selectedToken}
            onChange={(e) => setSelectedToken(e.target.value)}
            className="w-full p-3 bg-gray-700 border border-gray-600 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {allTokens.map(token => (
              <option key={token.address} value={token.address}>
                {token.name} ({token.symbol})
              </option>
            ))}
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-300">Amount</label>
          <input
            type="number"
            value={liquidityAmount}
            onChange={(e) => setLiquidityAmount(e.target.value)}
            min="0.1"
            step="0.1"
            className="w-full p-3 bg-gray-700 border border-gray-600 focus:border-blue-500 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        
        <button
          onClick={addLiquidity}
          disabled={loading}
          className="w-full bg-blue-700 hover:bg-blue-600 text-white p-3 rounded-md font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 border border-blue-600"
        >
          {loading ? "Processing..." : "Add Liquidity"}
        </button>
      </div>
      
      {/* Withdraw Liquidity Section */}
      <div className="mb-8 border-b border-gray-700 pb-6">
        <h3 className="text-lg font-bold mb-4 text-gray-200">Withdraw Liquidity</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-300">Token</label>
          <select 
            value={selectedToken}
            onChange={(e) => setSelectedToken(e.target.value)}
            className="w-full p-3 bg-gray-700 border border-gray-600 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {allTokens.map(token => (
              <option key={token.address} value={token.address}>
                {token.name} ({token.symbol})
              </option>
            ))}
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-300">Amount</label>
          <input
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            min="0.1"
            step="0.1"
            className="w-full p-3 bg-gray-700 border border-gray-600 focus:border-blue-500 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {riskInfo && (
            <p className="text-sm text-blue-300 mt-2">
              Available: {riskInfo.availableLiquidity} {selectedToken === SONIC_TOKEN ? 'S' : ''}
            </p>
          )}
        </div>
        
        <button
          onClick={withdrawLiquidity}
          disabled={loading}
          className="w-full bg-blue-700 hover:bg-blue-600 text-white p-3 rounded-md font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 border border-blue-600"
        >
          {loading ? "Processing..." : "Withdraw Liquidity"}
        </button>
      </div>
      
      {/* Manage Tokens Section */}
      <div>
        <h3 className="text-lg font-bold mb-4 text-gray-200">Manage Tokens</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-300">Token Address</label>
          <input
            type="text"
            value={newTokenAddress}
            onChange={(e) => setNewTokenAddress(e.target.value)}
            placeholder="0x..."
            className="w-full p-3 bg-gray-700 border border-gray-600 focus:border-blue-500 text-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-300">Allow Token</label>
          <div className="flex space-x-4">
            <button
              onClick={() => setNewTokenAllowed(true)}
              className={`flex-1 p-3 rounded-md shadow-sm transition-colors duration-200 ${
                newTokenAllowed 
                  ? 'bg-blue-700 text-white border border-blue-600' 
                  : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
              }`}
            >
              Allow
            </button>
            <button
              onClick={() => setNewTokenAllowed(false)}
              className={`flex-1 p-3 rounded-md shadow-sm transition-colors duration-200 ${
                !newTokenAllowed 
                  ? 'bg-blue-700 text-white border border-blue-600' 
                  : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
              }`}
            >
              Disallow
            </button>
          </div>
        </div>
        
        <button
          onClick={setTokenAllowed}
          disabled={loading || !newTokenAddress}
          className="w-full bg-blue-700 hover:bg-blue-600 text-white p-3 rounded-md font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 border border-blue-600"
        >
          {loading ? "Processing..." : "Update Token"}
        </button>
        
        {/* Token List */}
        <div className="mt-6">
          <h4 className="font-medium text-gray-300 mb-3">Allowed Tokens</h4>
          <div className="bg-gray-750 border border-gray-700 rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Token</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {allTokens.map(token => (
                  <tr key={token.address}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{token.name} ({token.symbol})</div>
                      <div className="text-xs text-gray-500 font-mono">{token.address}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        token.allowed ? 'bg-blue-900 text-blue-200' : 'bg-gray-700 text-gray-400'
                      }`}>
                        {token.allowed ? 'Allowed' : 'Disallowed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200">
      {/* Header */}
      <header className="bg-gray-800 bg-opacity-80 border-b border-blue-800 text-white p-4 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-300">Sonic CoinFlip</h1>
          
          {isConnected ? (
            <div className="flex items-center">
              <span className="hidden md:inline mr-2 text-blue-200 text-sm">
                {account.substring(0, 6)}...{account.substring(account.length - 4)}
                {isOwner && <span className="ml-2 bg-blue-800 text-blue-200 px-2 py-0.5 text-xs rounded-full">Owner</span>}
              </span>
              <div className="bg-blue-700 text-blue-100 px-3 py-1 rounded-md shadow-sm text-sm border border-blue-600">Connected</div>
            </div>
          ) : (
            <button
              onClick={connectWallet}
              disabled={loading}
              className="bg-blue-700 hover:bg-blue-600 border border-blue-500 text-white px-4 py-2 rounded-md shadow-sm transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? "Connecting..." : "Connect Wallet"}
            </button>
          )}
        </div>
      </header>
      
      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        {/* Error message */}
        {error && (
          <div className="bg-red-900 bg-opacity-30 border border-red-700 text-red-300 px-4 py-3 rounded-md mb-6 shadow-sm">
            {error}
          </div>
        )}
        
        {/* Not connected message */}
        {!isConnected && (
          <div className="bg-gray-800 border border-blue-800 text-gray-300 px-6 py-8 rounded-md mb-6 text-center shadow-md">
            <h2 className="text-xl font-bold mb-3 text-blue-300">Welcome to Sonic CoinFlip!</h2>
            <p className="mb-6">Connect your wallet to start flipping coins and winning.</p>
            <button
              onClick={connectWallet}
              className="bg-blue-700 hover:bg-blue-600 border border-blue-500 text-white px-6 py-2 rounded-md font-medium transition-colors duration-200 shadow-sm"
            >
              Connect Wallet
            </button>
          </div>
        )}
        
        {/* Tabs */}
        {isConnected && (
          <div className="mb-6">
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => setCurrentPage('play')}
                className={`px-5 py-3 font-medium transition-colors duration-200 ${
                  currentPage === 'play' 
                    ? 'border-b-2 border-blue-500 text-blue-300' 
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                Play
              </button>
              <button
                onClick={() => setCurrentPage('games')}
                className={`px-5 py-3 font-medium transition-colors duration-200 ${
                  currentPage === 'games' 
                    ? 'border-b-2 border-blue-500 text-blue-300' 
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                My Games
              </button>
              <button
                onClick={() => setCurrentPage('info')}
                className={`px-5 py-3 font-medium transition-colors duration-200 ${
                  currentPage === 'info' 
                    ? 'border-b-2 border-blue-500 text-blue-300' 
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                Info
              </button>
              {isOwner && (
                <button
                  onClick={() => setCurrentPage('admin')}
                  className={`px-5 py-3 font-medium transition-colors duration-200 ${
                    currentPage === 'admin' 
                      ? 'border-b-2 border-blue-500 text-blue-300' 
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Admin
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Tab content */}
        {isConnected && (
          <div>
            {currentPage === 'play' && renderPlayTab()}
            {currentPage === 'games' && renderMyGamesTab()}
            {currentPage === 'info' && renderInfoTab()}
            {isOwner && currentPage === 'admin' && renderAdminTab()}
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-800 bg-opacity-80 border-t border-blue-900 text-gray-400 py-5 text-center text-sm">
        <div className="container mx-auto px-4">
          <p className="text-gray-300">CoinFlip Game on Sonic Network • Contract: <span className="text-blue-400">{CONTRACT_ADDRESS}</span></p>
          <p className="mt-2">
            <a href={`https://sonicscan.org/address/${CONTRACT_ADDRESS}`} target="_blank" rel="noopener noreferrer" 
               className="text-blue-400 hover:text-blue-300 transition-colors duration-200">
              View on SonicScan
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CoinFlipApp;